import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Download, ChevronRight, X, DollarSign, Plus, Edit2 } from 'lucide-react';
import { api } from '../api/client';
import { useTenant } from '../context/TenantContext';
import { WorkItemEditor, AddWorkItemButton } from '../components/WorkItemEditor';
import { UpgradeBanner } from '../components/UpgradeBanner';
import { FilterBar2 } from '../components/filters';
import { PageErrorBoundary, PaneErrorBoundary } from '../components/ErrorBoundary';
import { SkeletonTable, SkeletonKPIGrid, SkeletonPaneContent } from '../components/Skeleton';
import { NoFeaturesState, NoResultsState, NoCostDataState } from '../components/EmptyState';
import { normalizeFeature, toNumber, formatCurrency as safeCurrency, clamp } from '../utils/dataAdapters';

const STATUS_COLORS = {
  'Blocked': '#8E44AD', 'Behind': '#E74C3C', 'Due Date at Risk': '#F1C40F',
  'In Progress': '#3498DB', 'In Review': '#16A085', 'Not Started': '#BDC3C7',
  'Completed': '#2ECC71', 'Not Required': '#95A5A6', 'Unknown': '#9CA3AF',
};

const FeaturesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canEdit, isLowerTier } = useTenant();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [featureDetails, setFeatureDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  
  // New Filter Bar 2.0 state
  const [advancedFilters, setAdvancedFilters] = useState(() => {
    const status = searchParams.get('status');
    const epic_id = searchParams.get('epic_id');
    return {
      status: status ? status.split(',') : [],
      epic_id: epic_id ? [epic_id] : [],
    };
  });
  const [quickFilters, setQuickFilters] = useState({
    blocked_only: false,
    behind_only: false,
    at_risk_only: false,
    due_this_week: false,
  });
  const [filterOptions, setFilterOptions] = useState({ epics: [] });
  
  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [selectedEpicForCreate, setSelectedEpicForCreate] = useState(null);

  useEffect(() => { loadFilters(); }, []);
  
  // Reload features when filters change
  useEffect(() => {
    loadFeatures();
    // Update URL with current filters
    const params = new URLSearchParams();
    if (advancedFilters.status?.length > 0) params.set('status', advancedFilters.status.join(','));
    if (advancedFilters.epic_id?.length > 0) params.set('epic_id', advancedFilters.epic_id[0]);
    setSearchParams(params, { replace: true });
  }, [advancedFilters, quickFilters]);

  const loadFilters = async () => {
    try {
      const res = await api.getFilters();
      setFilterOptions({ epics: res.data?.epics || [] });
    } catch (err) { 
      console.error('Failed to load filters:', err); 
    }
  };

  const loadFeatures = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      
      // Multi-select filters
      if (advancedFilters.status?.length > 0) params.status = advancedFilters.status.join(',');
      if (advancedFilters.epic_id?.length > 0) params.epic_id = advancedFilters.epic_id[0];
      if (advancedFilters.project_id?.length > 0) params.project_id = advancedFilters.project_id[0];
      
      // Quick filters override status
      if (quickFilters.blocked_only) params.status = 'Blocked';
      if (quickFilters.behind_only) params.status = 'Behind';
      if (quickFilters.at_risk_only) params.status = 'Behind,Due Date at Risk,Blocked';
      
      const res = await api.getFeatures(params);
      // Normalize all features with safe defaults
      const rawFeatures = res.data?.features || res.data || [];
      setFeatures(rawFeatures.filter(Boolean).map(normalizeFeature).filter(Boolean));
    } catch (err) { 
      console.error('Failed to load features:', err);
      setError(err);
    }
    finally { setLoading(false); }
  };

  const loadFeatureDetails = async (id) => {
    if (!id) return;
    try {
      setDetailsLoading(true);
      setDetailsError(null);
      const res = await api.getFeatureDetails(id);
      setFeatureDetails(res.data);
    } catch (err) { 
      console.error('Failed to load feature details:', err);
      setDetailsError(err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleFeatureClick = (feature) => {
    if (!feature?.id) return;
    setSelectedFeature(feature);
    setFeatureDetails(null);
    loadFeatureDetails(feature.id);
  };

  const formatCurrency = (v) => safeCurrency(v, '$');

  const kpis = {
    total: features.length,
    avgComplete: features.length > 0 ? Math.round(features.reduce((s, f) => s + toNumber(f?.percent_complete), 0) / features.length) : 0,
    blocked: features.filter(f => f && toNumber(f.blocked_count) > 0).length,
    behind: features.filter(f => f?.status === 'Behind').length,
    totalVariance: features.reduce((s, f) => s + toNumber(f?.variance), 0)
  };

  // Editor handlers
  const handleAddFeature = () => {
    setEditingFeature(null);
    const epicId = advancedFilters.epic_id?.[0] || (filterOptions.epics[0]?.id);
    setSelectedEpicForCreate(epicId);
    setShowEditor(true);
  };

  const handleEditFeature = (feature) => {
    setEditingFeature(feature);
    setSelectedEpicForCreate(feature.epic_id);
    setShowEditor(true);
  };

  const handleEditorSave = () => {
    loadFeatures();
  };

  const handleEditorDelete = (id) => {
    setFeatures(features.filter(f => f.id !== id));
  };

  return (
    <div>
      {isLowerTier() && <UpgradeBanner feature="in-app feature editing" className="mb-6" />}
      
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
            <Box className="w-7 h-7 text-[var(--clr-sev-healthy)]" />
            Features
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">Manage and track all features across portfolios</p>
        </div>
        <div className="flex items-center gap-3">
          <AddWorkItemButton itemType="feature" onClick={handleAddFeature} />
          <a href={api.exportCSV('features', { 
            ...(advancedFilters.status?.length > 0 ? { status: advancedFilters.status.join(',') } : {}),
            ...(advancedFilters.epic_id?.length > 0 ? { epic_id: advancedFilters.epic_id[0] } : {}),
          })} className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-surface-subtle)] hover:bg-gray-200 rounded-lg text-sm font-medium" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </a>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Total Features</p><p className="text-2xl font-bold">{kpis.total}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Avg % Complete</p><p className="text-2xl font-bold text-[var(--clr-brand-primary)]">{kpis.avgComplete}%</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">With Blockers</p><p className="text-2xl font-bold text-purple-600">{kpis.blocked}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Behind Schedule</p><p className="text-2xl font-bold text-[var(--clr-sev-blocked)]">{kpis.behind}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Cost Variance</p><p className={`text-2xl font-bold ${kpis.totalVariance > 0 ? 'text-[var(--clr-sev-blocked)]' : 'text-[var(--clr-sev-healthy)]'}`}>{kpis.totalVariance > 0 ? '+' : ''}{formatCurrency(kpis.totalVariance)}</p></div>
      </div>

      {/* Filter Bar 2.0 */}
      <FilterBar2
        pageType="features"
        filters={advancedFilters}
        onFiltersChange={setAdvancedFilters}
        quickFilters={quickFilters}
        onQuickFiltersChange={setQuickFilters}
        counts={{
          blocked: kpis.blocked,
          behind: kpis.behind,
          atRisk: features.filter(f => f && ['Behind', 'Due Date at Risk', 'Blocked'].includes(f.status)).length,
        }}
        showInsightsBar={true}
        className="mb-6"
      />

      <div className="bg-[var(--clr-surface-card)] rounded-lg border overflow-hidden">
        {loading ? (
          <SkeletonTable rows={8} columns={7} />
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64 text-center p-6">
            <Box className="w-12 h-12 text-red-300 mb-4" />
            <p className="text-gray-600 font-medium mb-2">Failed to load features</p>
            <p className="text-sm text-gray-500 mb-4">{error.message || 'An error occurred'}</p>
            <button onClick={loadFeatures} className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg text-sm">
              Try Again
            </button>
          </div>
        ) : features.length === 0 ? (
          advancedFilters.status?.length > 0 || advancedFilters.epic_id?.length > 0 ? (
            <NoResultsState 
              onClear={() => {
                setAdvancedFilters({ status: [], epic_id: [] });
                setQuickFilters({ blocked_only: false, behind_only: false, at_risk_only: false, due_this_week: false });
              }}
            />
          ) : (
            <NoFeaturesState onCreate={handleAddFeature} />
          )
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--clr-surface-subtle)] border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Feature</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Epic</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Project</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">% Complete</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Est Cost</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Variance</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {features.filter(Boolean).map((f) => {
                  const isMatchedItem = f._is_matched === true;
                  const isParentContext = f._is_parent === true && !f._is_matched;
                  
                  return (
                  <tr 
                    key={f.id} 
                    onClick={() => handleFeatureClick(f)} 
                    className={`hover:bg-[var(--clr-surface-hover)] cursor-pointer ${isMatchedItem ? 'bg-yellow-50 border-l-4 border-l-yellow-400' : ''}`}
                    data-testid={`feature-row-${f.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isMatchedItem && <span className="px-1.5 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">Match</span>}
                        <span className="font-medium text-[var(--clr-text-primary)]">{f.name}</span>
                      </div>
                      <div className="text-xs text-[var(--clr-text-muted)]">{f.portfolio_name} / {f.program_name}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{f.epic_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{f.project_name}</td>
                    <td className="px-4 py-3"><span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium text-white" style={{ backgroundColor: STATUS_COLORS[f.status] || '#95A5A6' }}>{f.status || 'Unknown'}</span></td>
                    <td className="px-4 py-3"><div className="flex items-center gap-2"><div className="w-16 bg-gray-200 rounded-full h-2"><div className="bg-[var(--clr-brand-primary)] h-2 rounded-full" style={{ width: `${f.percent_complete || 0}%` }}></div></div><span className="text-sm font-medium">{f.percent_complete || 0}%</span></div></td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{formatCurrency(f.est_cost)}</td>
                    <td className="px-4 py-3"><span className={`text-sm font-medium ${(f.variance || 0) > 0 ? 'text-[var(--clr-sev-blocked)]' : 'text-[var(--clr-sev-healthy)]'}`}>{(f.variance || 0) > 0 ? '+' : ''}{formatCurrency(f.variance)}</span></td>
                    <td className="px-4 py-3"><ChevronRight className="w-4 h-4 text-[var(--clr-text-muted)]" /></td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedFeature && (
        <div className="fixed inset-0 z-50 flex" data-testid="feature-detail-pane">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSelectedFeature(null)}></div>
          <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold text-[var(--clr-text-primary)]">{selectedFeature.name || 'Feature Details'}</h2>
                <p className="text-sm text-[var(--clr-text-muted)]">
                  {selectedFeature.epic_name || '-'} → {selectedFeature.project_name || '-'}
                </p>
              </div>
              <button onClick={() => setSelectedFeature(null)} className="p-2 hover:bg-[var(--clr-surface-hover)] rounded-lg" data-testid="close-pane-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
            <PaneErrorBoundary paneName="feature-details" onClose={() => setSelectedFeature(null)}>
              {detailsLoading ? (
                <SkeletonPaneContent />
              ) : detailsError ? (
                <div className="flex flex-col items-center justify-center h-64 p-6 text-center">
                  <Box className="w-10 h-10 text-red-300 mb-3" />
                  <p className="text-gray-600 font-medium mb-2">Failed to load details</p>
                  <button onClick={() => loadFeatureDetails(selectedFeature.id)} className="text-sm text-[var(--clr-brand-primary)]">
                    Try Again
                  </button>
                </div>
              ) : featureDetails?.feature ? (
                <div className="p-6 space-y-6">
                  {/* Status and Progress */}
                  <div className="flex items-center gap-4">
                    <span 
                      className="px-3 py-1 rounded-full text-sm font-medium text-white" 
                      style={{ backgroundColor: STATUS_COLORS[featureDetails.feature.status] || STATUS_COLORS['Unknown'] }}
                    >
                      {featureDetails.feature.status || 'Unknown'}
                    </span>
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span>Progress</span>
                        <span className="font-medium">{clamp(featureDetails.feature.percent_complete, 0, 100)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-[var(--clr-brand-primary)] h-2 rounded-full" 
                          style={{ width: `${clamp(featureDetails.feature.percent_complete, 0, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Timeline */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                      <p className="text-xs text-[var(--clr-text-muted)] mb-1">Start</p>
                      <p className="text-sm font-medium text-[var(--clr-text-primary)]">{featureDetails.feature.start_date || '-'}</p>
                    </div>
                    <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                      <p className="text-xs text-[var(--clr-text-muted)] mb-1">Due</p>
                      <p className="text-sm font-medium text-[var(--clr-text-primary)]">{featureDetails.feature.due_date || '-'}</p>
                    </div>
                    <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                      <p className="text-xs text-[var(--clr-text-muted)] mb-1">Revised</p>
                      <p className="text-sm font-medium text-[var(--clr-text-primary)]">{featureDetails.feature.revised_date || featureDetails.feature.revised_due_date || '-'}</p>
                    </div>
                  </div>
                  
                  {/* Cost Summary - with proper contrast colors */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-[var(--clr-text-secondary)]" />
                      Cost Summary
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      {/* Estimated - Blue theme with proper contrast */}
                      <div className="metric-card metric-brand rounded-lg p-3" data-testid="cost-estimated">
                        <p className="metric-card-label text-xs mb-1">Estimated</p>
                        <p className="metric-card-value text-lg font-bold">
                          {formatCurrency(featureDetails.feature.cost_metrics?.est_cost)}
                        </p>
                      </div>
                      {/* Actual - Green theme */}
                      <div className="metric-card metric-success rounded-lg p-3" data-testid="cost-actual">
                        <p className="metric-card-label text-xs mb-1">Actual</p>
                        <p className="metric-card-value text-lg font-bold">
                          {formatCurrency(featureDetails.feature.cost_metrics?.act_cost)}
                        </p>
                      </div>
                      {/* Variance - Red/Green based on value */}
                      <div 
                        className={`metric-card rounded-lg p-3 ${toNumber(featureDetails.feature.cost_metrics?.variance) > 0 ? 'metric-danger' : 'metric-success'}`}
                        data-testid="cost-variance"
                      >
                        <p className="metric-card-label text-xs mb-1">Variance</p>
                        <p className="metric-card-value text-lg font-bold">
                          {toNumber(featureDetails.feature.cost_metrics?.variance) > 0 ? '+' : ''}
                          {formatCurrency(featureDetails.feature.cost_metrics?.variance)}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Work Items */}
                  {Array.isArray(featureDetails.work_items) && featureDetails.work_items.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Work Items ({featureDetails.work_items.length})</h3>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {featureDetails.work_items.slice(0, 20).map((wi, idx) => (
                          <div key={wi?.id || idx} className="flex items-center justify-between p-2 bg-[var(--clr-surface-subtle)] rounded-lg text-sm">
                            <span className="truncate flex-1 text-[var(--clr-text-primary)]">{wi?.task || wi?.story || 'Item'}</span>
                            <span 
                              className="px-2 py-0.5 rounded text-xs font-medium text-white ml-2" 
                              style={{ backgroundColor: STATUS_COLORS[wi?.status] || STATUS_COLORS['Unknown'] }}
                            >
                              {wi?.status || 'Unknown'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Navigation Links */}
                  <div className="pt-4 border-t space-y-2">
                    <button 
                      onClick={() => navigate(`/epics?project_id=${featureDetails.feature?.project_id || ''}`)} 
                      className="w-full text-left px-4 py-2 bg-[var(--clr-surface-subtle)] hover:bg-gray-200 rounded-lg text-sm font-medium text-[var(--clr-text-primary)]"
                    >
                      Open Epic →
                    </button>
                    <button 
                      onClick={() => navigate(`/stories?feature_id=${selectedFeature?.id || ''}`)} 
                      className="w-full text-left px-4 py-2 bg-[var(--clr-surface-subtle)] hover:bg-gray-200 rounded-lg text-sm font-medium text-[var(--clr-text-primary)]"
                    >
                      View Stories →
                    </button>
                  </div>
                </div>
              ) : (
                <NoCostDataState variant="pane" />
              )}
            </PaneErrorBoundary>
          </div>
        </div>
      )}

      {/* Work Item Editor Modal */}
      <WorkItemEditor
        isOpen={showEditor}
        onClose={() => { setShowEditor(false); setEditingFeature(null); }}
        itemType="feature"
        item={editingFeature}
        parentId={selectedEpicForCreate}
        projectId={editingFeature?.project_id || features[0]?.project_id || advancedFilters.project_id?.[0]}
        onSave={handleEditorSave}
        onDelete={handleEditorDelete}
      />
    </div>
  );
};

// Wrap with PageErrorBoundary for top-level error handling
const FeaturesPageWithErrorBoundary = () => (
  <PageErrorBoundary pageName="Features">
    <FeaturesPage />
  </PageErrorBoundary>
);

export default FeaturesPageWithErrorBoundary;
